import React, {useContext, useEffect, useRef, useState, useMemo} from 'react';
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {LoadingContext} from "../../context/LoadingContext";
import './ProgramDetail.scss';

const ProgramDetail = ({id}) => {
    const { programId } = useParams();
    const navigate = useNavigate();
    const vidId = programId || id;
    const {showLoading, hideLoading} = useContext(LoadingContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);
    const [copied, setCopied] = useState(false);
    const playerRef = useRef(null);

    const getEmbedUrl = (urlOrId) => {
        if (!urlOrId) return '';
        // eğer sadece id geldiyse
        if (/^[A-Za-z0-9_-]{6,}$/.test(urlOrId) && !urlOrId.includes('http')) {
            return `https://www.youtube.com/embed/${urlOrId}?rel=0&playsinline=1`;
        }
        try {
            const u = new URL(urlOrId);
            const host = u.hostname.toLowerCase();

            if (host.includes('youtube.com')) {
                const v = u.searchParams.get('v');
                if (v) return `https://www.youtube.com/embed/${v}?rel=0&playsinline=1`;
                if (u.pathname.startsWith('/embed/')) return urlOrId;
            }

            if (host.includes('youtu.be')) {
                const id = u.pathname.replace(/^\/+/, '');
                if (id) return `https://www.youtube.com/embed/${id}?rel=0&playsinline=1`;
            }

            return urlOrId;
        } catch (e) {
            return urlOrId;
        }
    };

    const getWatchUrl = (urlOrId) => {
        if (!urlOrId) return '#';
        if (/^[A-Za-z0-9_-]{6,}$/.test(urlOrId) && !urlOrId.includes('http')) return `https://youtu.be/${urlOrId}`;
        try {
            const u = new URL(urlOrId);
            const host = u.hostname.toLowerCase();
            if (host.includes('youtube.com') || host.includes('youtu.be')) return urlOrId;
            return urlOrId;
        } catch (e) {
            return urlOrId;
        }
    };

    const findVideoInPlaylists = (playlists = [], idToFind) => {
        if (!idToFind) return null;
        for (const pl of playlists || []) {
            if (!pl || !Array.isArray(pl.videos)) continue;
            const v = pl.videos.find(
                (video) =>
                    video.id === idToFind ||
                    video.videoId === idToFind ||
                    (video.url && typeof video.url === "string" && video.url.includes(idToFind))
            );
            if (v) return { ...v, playlist: pl };
        }
        return null;
    };

    const getData = async () => {
        if (!vidId) return;
        setError(null);
        setLoading(true);
        try {
            showLoading && showLoading();
            const res = await axios.get("https://obbtv.com/ytbot/");
            // debug: API cevabını görmek için geçici log
            console.debug('ytbot response:', res?.data);
            const found = findVideoInPlaylists(res.data?.playlists, vidId);
            if (!found) {
                setError('Video bulunamadı.');
                setData(null);
                setActiveVideo(null);
            } else {
                // data olarak playlist'i tut; activeVideo olarak bulunan videoyu ayarla
                setData(found.playlist ? found.playlist : res.data);
                setActiveVideo(found);
            }
        } catch (err) {
            console.error(err);
            setError('Veri alınırken hata oluştu.');
            setData(null);
            setActiveVideo(null);
        } finally {
            hideLoading && hideLoading();
            setLoading(false);
        }
    };

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vidId]);

    const getThumbnail = (d) => d?.thumbnail || (d?.playlist?.thumbnail) || (d?.id ? `https://i.ytimg.com/vi/${d.id}/hqdefault.jpg` : '');

    // normalize playlist - API dönüşü farklı şekillerde olabiliyor
    const playlist = data?.playlist || data || null;
    // videoları güvenli almak için fallback: playlist içindeki videolar veya activeVideo.playlist
    const videos = useMemo(() => {
        if (playlist && Array.isArray(playlist.videos)) return playlist.videos;
        if (activeVideo?.playlist && Array.isArray(activeVideo.playlist.videos)) return activeVideo.playlist.videos;
        return [];
    }, [playlist, activeVideo]);

    // Debug: playlist/videos durumunu izleyin (geçici)
    useEffect(() => {
        console.debug('playlist normalized:', playlist);
        console.debug('videos length:', videos?.length, videos?.slice?.(0,3));
        console.debug('activeVideo:', activeVideo);
    }, [playlist, videos, activeVideo]);

    const handlePlayFromSidebar = (e, video) => {
        e.preventDefault();
        e.stopPropagation();
        if (!video) return;
        if (!video.playlist) video.playlist = data?.playlist || data;
        setActiveVideo(video);
        try { playerRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'}); } catch (err) {}
    };

    const handleCopyLink = async () => {
        const link = activeVideo?.id ? `${window.location.origin}/programlar/${activeVideo.id}` : activeVideo?.url || '';
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(link);
            } else {
                const ta = document.createElement('textarea');
                ta.value = link;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                ta.remove();
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error('Kopyalama başarısız', e);
        }
    };

    return (
        <div className="program-detail-page">


            <div className="program-main">
                <section className="program-player">
                    <div className="player-wrapper" ref={playerRef}>
                        {loading ? (
                            <div className="skeleton-player">
                                <div className="skeleton-title" />
                                <div className="skeleton-media" />
                            </div>
                        ) : activeVideo?.url || activeVideo?.id ? (
                            <>
                                <iframe
                                    title={activeVideo?.title || 'YouTube Player'}
                                    src={getEmbedUrl(activeVideo?.url || activeVideo?.id)}
                                    style={{border:0}}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />

                                {/* Player toolbar (YouTube open / copy link) */}
                                <div className="player-toolbar">
                                    <a
                                        className="open-youtube"
                                        href={ getWatchUrl(activeVideo?.url || activeVideo?.id) }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="YouTube'da aç"
                                    >
                                        YouTube
                                    </a>

                                    <button
                                        className="copy-link"
                                        onClick={handleCopyLink}
                                        aria-pressed={copied}
                                        aria-label="Bağlantıyı kopyala"
                                    >
                                        {copied ? 'Kopyalandı' : 'Bağlantıyı Kopyala'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="player-placeholder">
                                <img src={getThumbnail(activeVideo || data)} alt={(activeVideo || data)?.title || 'thumbnail'} />
                            </div>
                        )}
                    </div>

                    <div className="player-meta">
                        {error && <div className="program-error">{error}</div>}
                        <h2 className="video-title">{activeVideo?.title || data?.title}</h2>
                        <div className="meta-row">
                            {activeVideo?.playlist && <div className="video-playlist">Seri: {activeVideo.playlist.title}</div>}
                            <div className="meta-spacer" />
                            {activeVideo?.published && <div className="video-date">{activeVideo.published}</div>}
                        </div>
                    </div>
                </section>

                <aside className="program-sidebar">
                    <h3 className="sidebar-title">Diğer Bölümler</h3>
                    <div className="other-list">
                        {loading ? (
                            Array.from({length:4}).map((_,i) => (
                                <div className="other-item skeleton" key={i}>
                                    <div className="other-item-thumb"><div className="skeleton-thumb"/></div>
                                    <div className="other-item-info">
                                        <div className="skeleton-line short"/>
                                        <div className="skeleton-line"/>
                                    </div>
                                </div>
                            ))
                        ) : (
                            videos && videos.length > 0 ? (
                                videos.map((video) => (
                                    <div className={`other-item ${activeVideo?.id === video.id ? 'active' : ''}`} key={video.id}>
                                        <Link to={`/programlar/${video.id}`} className="other-item-link" onClick={(e)=>{ /* link handled by router */ }}>
                                            <div className="other-item-thumb">
                                                <img src={video.thumbnail} alt={video.title} loading="lazy" />
                                                <button className="other-play-btn" onClick={(e)=>handlePlayFromSidebar(e, {...video, playlist: data?.playlist})} aria-label={`Oynat ${video.title}`}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="#fff"/></svg>
                                                </button>
                                                <span className="duration">{video.duration || ''}</span>
                                            </div>
                                            <div className="other-item-info">
                                                <div className="other-item-title">{video.title}</div>
                                            </div>
                                        </Link>
                                    </div>
                                ))
                            ) : (
                                <div className="other-empty">Diğer bölüm bulunamadı.</div>
                            )
                        )}
                     </div>
                </aside>
            </div>

            {(activeVideo?.description || data?.description) && (
                <div className="program-description">
                    <h4>Açıklama</h4>
                    <div dangerouslySetInnerHTML={{__html: activeVideo?.description || data.description}} />
                </div>
            )}

            {/* toast */}
            <div className={`pd-toast ${copied ? 'show' : ''}`} role="status" aria-live="polite">{copied ? 'Bağlantı kopyalandı' : ''}</div>

        </div>
    );
};

export default ProgramDetail;

